import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiClock, FiCheck } from 'react-icons/fi';

interface UpdateStatusModalProps {
  task: any | null;
  selectedStatus: 'To Do' | 'In Progress' | 'Complete';
  onSelectStatus: (status: 'To Do' | 'In Progress' | 'Complete') => void;
  onConfirm: () => void;
  onClose: () => void;
}

export const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  task,
  selectedStatus,
  onSelectStatus,
  onConfirm,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {task && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-md bg-[#0B0B0B] border border-white/10 rounded-[28px] p-5 sm:p-6 text-left flex flex-col max-h-[82vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Sticky Header */}
            <div className="shrink-0 flex items-start justify-between pb-4 mb-4 border-b border-white/5 bg-[#0B0B0B] z-20">
              <div>
                <h2 className="text-xl font-bold text-white">Update Status</h2>
                <p className="text-zinc-550 text-xs mt-1">Move this task to its next stage</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
              <div>
                <label className="block text-zinc-550 text-xs mb-2 font-medium">Current task</label>
                <div className="w-full bg-[#121212] border border-white/5 rounded-2xl px-4 py-3.5 text-white text-xs font-bold">
                  {task.title}
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-semibold mb-2">Status</label>
                <div className="space-y-2.5">
                  {(['To Do', 'In Progress', 'Complete'] as const).map((st) => {
                    const isSelected = selectedStatus === st;
                    const isCurrent = task.status === st;

                    return (
                      <button
                        key={st}
                        type="button"
                        onClick={() => onSelectStatus(st)}
                        className={`w-full flex items-center justify-between p-3.5 rounded-2xl border transition-all cursor-pointer ${
                          isSelected
                            ? 'border-blue-500/80 bg-[#121212] ring-1 ring-blue-500/80'
                            : 'border-white/5 bg-[#121212] hover:border-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {st === 'To Do' && (
                            <div className="w-4 h-4 rounded-full border border-zinc-600 flex items-center justify-center text-transparent" />
                          )}
                          {st === 'In Progress' && <FiClock className="w-4 h-4 text-blue-400" />}
                          {st === 'Complete' && <FiCheck className="w-4 h-4 text-emerald-400" />}
                          <span
                            className={`text-xs font-bold ${
                              st === 'To Do'
                                ? 'text-zinc-200'
                                : st === 'In Progress'
                                  ? 'text-blue-400'
                                  : 'text-emerald-400'
                            }`}
                          >
                            {st}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {isCurrent && (
                            <span className="text-[10px] font-bold text-zinc-400 bg-zinc-800 px-2.5 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                          <div
                            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                              isSelected ? 'border-blue-500 bg-blue-500' : 'border-zinc-600'
                            }`}
                          >
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="shrink-0 flex items-center justify-end pt-4 mt-4 border-t border-white/5 bg-[#0B0B0B] z-20">
              <button
                type="button"
                onClick={onConfirm}
                className="px-6 py-2.5 bg-white text-black hover:bg-zinc-100 rounded-full text-xs font-bold cursor-pointer transition-all active:scale-95 shadow-lg shadow-white/5"
              >
                Update Status
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
